import { Component, createRef, Fragment } from 'preact';
import { motion } from 'framer-motion';
import { sorceryImageUrl } from '../utils/cardImageUrl';
import { Mail, Users } from 'lucide-react';
import { CURRENCY, isArenaDebugMode } from '../utils/arena/profileDefaults';
import {
  BOOSTER_SETS,
  SEALED_BOXES,
  SITES_BOOSTERS,
  isSitesBoosterKey,
} from '../utils/arena/packsApi';
import {
  ALPHA_PRECONS,
  ALPHA_PRECON_ORDER,
  ALPHA_PRECON_PRICE,
  isAlphaPreconKey,
} from '../utils/arena/alphaPrecons';
import { getLocalApiOrigin } from '../utils/localApi';
import { cn } from '../lib/utils';
import { GOLD, GOLD_TEXT, ACCENT_GOLD, BEVELED_BTN, GOLD_BTN, PANEL_BG, PANEL_BORDER, TEXT_PRIMARY, CornerPlating } from '../lib/medievalTheme';
import AppHeader from './AppHeader';
import { TabBar } from './ui/TabBar';
import { screenPanel } from '../utils/screenEnter';
import { playUI, UI } from '../utils/arena/uiSounds';
import AmbientParticles from './AmbientParticles';
import StoreTorchFX from './StoreTorchFX';
import CandleFlames from './CandleFlames';
import CoverArtCanvas from './CoverArtCanvas';
import CandlePlacementDev from './CandlePlacementDev';
import { isDevBuild } from '../utils/devMode';
import { k, t } from '../localization/t';

// Exported so the pack opener can light the same room. Its backdrop is this
// same artwork, so the candle positions. Which are image-relative. Carry
// over unchanged.
export const STORE_DEFAULT_CANDLES = [
  { left: '80.8%', top: '56.2%', size: 11, glow: 70, speed: 3.2, phase: 3.7426277332618003 },
  { left: '82.4%', top: '54.3%', size: 11, glow: 70, speed: 3.2, phase: 2.696314252899764 },
  { left: '90.5%', top: '53.6%', size: 11, glow: 70, speed: 3.2, phase: 0.4753711200308055 },
  { left: '9.8%', top: '64.1%', size: 11, glow: 70, speed: 3.2, phase: 0.4365173902277124 },
  { left: '10.7%', top: '64.1%', size: 11, glow: 70, speed: 3.2, phase: 2.062619939542515 },
];

import StoreSinglesTab from './StoreSinglesTab';
import StoreDisenchantTab from './StoreDisenchantTab';
import StoreCosmeticsTab from './StoreCosmeticsTab';
import { CoinIcon } from './ui/icons';
import { BackButton } from './ui/navButtons';
import { ScrollArea } from './ui/scroll-area';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';
import { ProductPreview } from './ProductPreview';
import PreconDeckBox3D from './PreconDeckBox3D';

const STORE_TUTORIAL_KEY = 'store-v2';

const SET_ORDER = ['beta', 'arthurian', 'gothic'];
const BOX_ORDER = ['dragonlord'];
const SITES_BOOSTER_ORDER = ['sitesBeta', 'sitesArthurian', 'sitesGothic'];

export function getStoreTabs() {
  return [
    { id: 'boosters', label: t('Sealed') },
    { id: 'singles', label: t('Spellcraft') },
    { id: 'cosmetics', label: t('Cosmetics') },
    { id: 'disenchant', label: t('Disenchant') },
  ];
}

const SEALED_SUB_TABS = [
  { id: 'boosters', label: k('Booster Packs') },
  { id: 'boxes', label: k('Boxes') },
];


function getBoosterImage(setKey) {
  const base = getLocalApiOrigin();
  return `${base}/game-assets/booster-${setKey}.webp`;
}

function getBoxImage(boxKey) {
  const base = getLocalApiOrigin();
  return `${base}/game-assets/box-${boxKey}.webp`;
}

// Resolve product art. Sites Boosters reuse their source set's regular
// booster art with a "SITES" badge layered on top by the Sites tab
// renderer.
function getProductImage(setKey) {
  if (ALPHA_PRECONS[setKey]) {
    // Precons render via the 3D box component, but the cart line
    // and confirm-dialog thumbnail need a flat image too. Use the
    // avatar art directly.
    return sorceryImageUrl(ALPHA_PRECONS[setKey].avatarSlug);
  }
  if (SEALED_BOXES[setKey]) return getBoxImage(setKey);
  if (isSitesBoosterKey(setKey)) {
    return getBoosterImage(SITES_BOOSTERS[setKey].sourceSetKey);
  }
  return getBoosterImage(setKey);
}

function getProductLabel(setKey) {
  return (
    ALPHA_PRECONS[setKey]?.label ||
    SEALED_BOXES[setKey]?.label ||
    SITES_BOOSTERS[setKey]?.label ||
    BOOSTER_SETS[setKey]?.label ||
    setKey
  );
}

function getProductUnitPrice(setKey) {
  return (
    (ALPHA_PRECONS[setKey] ? ALPHA_PRECON_PRICE : undefined) ??
    SEALED_BOXES[setKey]?.price ??
    SITES_BOOSTERS[setKey]?.price ??
    CURRENCY.PACK_PRICE
  );
}

const ALL_PRODUCT_KEYS = [
  ...SET_ORDER,
  ...BOX_ORDER,
  ...ALPHA_PRECON_ORDER,
  ...SITES_BOOSTER_ORDER,
];

function emptyCart() {
  const cart = {};
  for (const key of ALL_PRODUCT_KEYS) cart[key] = 0;
  return cart;
}

// Price a cart without mounting the store. The mobile store view needs
// the totals but not the 3D product tiles, so this stays a pure
// function over the shared price table; getCartTotal() delegates to it
// rather than keeping a second copy of the loop.
export function getStoreCartSummary(cart) {
  let items = 0;
  let cost = 0;
  for (const key of ALL_PRODUCT_KEYS) {
    const qty = cart?.[key] || 0;
    items += qty;
    cost += qty * getProductUnitPrice(key);
  }
  return { items, cost };
}

const BOOSTER_SCALE = {
  gothic: 1,
  arthurian: 1.85,
  beta: 1,
};

export default class ArenaStore extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'boosters',
      // Sub-tab inside the Sealed Product view. 'boosters' lists the
      // randomized booster packs; 'boxes' lists fixed-content sealed
      // boxes like the Dragonlord Box. The cart is shared across both
      // so a player can check out with a mix in one transaction.
      sealedSubTab: 'boosters',
      cart: emptyCart(),
      showConfirm: false,
      purchaseFlash: false,
      showStoreTutorial: false,
      candles: STORE_DEFAULT_CANDLES,
    };
  }

  packImgRefs = {};
  canvasRef = createRef();

  componentDidMount() {
    // First-run onboarding. Deferred so the booster grid lays out
    // before the overlay measures its targets. Awaits tutorial
    // state hydration first so the on-disk seen flag is honoured.
    const profileId = this.props.profile?.id;
    if (profileId) {
      hydrateTutorialState().then(() => {
        if (this._unmounted) return;
        if (!shouldAutoPlay(profileId, STORE_TUTORIAL_KEY)) return;
        this._tutorialTimer = setTimeout(() => {
          if (!this._unmounted) this.setState({ showStoreTutorial: true });
        }, 500);
      });
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
  }

  handlePackMove = (setKey, e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    const rotY = (mx - 0.5) * 18;
    const rotX = (0.5 - my) * 14;
    const img = this.packImgRefs[setKey];
    if (img) {
      const baseScale = BOOSTER_SCALE[setKey] || 1;
      img.style.transform = `perspective(700px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(${baseScale * 1.04})`;
    }
  };

  handlePackEnter = (setKey) => {
    const img = this.packImgRefs[setKey];
    if (img) img.style.transition = 'transform 0.12s ease-out';
  };

  handlePackLeave = (setKey) => {
    const img = this.packImgRefs[setKey];
    if (img) {
      const baseScale = BOOSTER_SCALE[setKey] || 1;
      img.style.transition = 'transform 0.45s ease-out';
      img.style.transform = `perspective(700px) rotateX(0deg) rotateY(0deg) scale(${baseScale})`;
    }
  };

  handleStoreTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, STORE_TUTORIAL_KEY);
    this.setState({ showStoreTutorial: false });
  };

  setCart = (setKey, value) => {
    const qty = Math.max(0, Math.min(99, parseInt(value, 10) || 0));
    this.setState((s) => ({ cart: { ...s.cart, [setKey]: qty } }));
  };

  addToCart = (setKey) => {
    this.setState((s) => ({ cart: { ...s.cart, [setKey]: (s.cart[setKey] || 0) + 1 } }));
  };

  getCartTotal() {
    const { items, cost } = getStoreCartSummary(this.state.cart);
    return { packs: items, cost };
  }

  buildTutorialSteps() {
    const goToTab = (tab, sealedSubTab) => () => {
      this.setState((s) => ({
        tab,
        sealedSubTab: sealedSubTab || s.sealedSubTab,
      }));
    };
    return [
      {
        key: 'welcome',
        title: t('Welcome to the Store'),
        body: t('Four tabs sit at the top: Sealed Product, Spellcraft, Cosmetics and Disenchant. Valkenhall is free-to-play: every currency comes from playing the game.'),
        onEnter: goToTab('boosters', 'boosters'),
      },
      {
        key: 'sealed-tab',
        title: t('Sealed Product'),
        body: t('Booster packs and sealed boxes live here. Spend gold and crack them open for cards.'),
        selector: '[data-tutorial="store-tab-boosters"]',
        onEnter: goToTab('boosters', 'boosters'),
      },
      {
        key: 'sealed-subtabs',
        title: t('Booster Packs and Boxes'),
        body: t('Switch between regular Booster Packs and fixed-content Boxes. Sites Boosters in the Booster Packs view let you target set-specific sites. The basic sites you need for your Atlas are already in your collection.'),
        selector: '[data-tutorial="store-sealed-subtabs"]',
        onEnter: goToTab('boosters', 'boosters'),
      },
      {
        key: 'spellcraft-tab',
        title: t('Spellcraft'),
        body: t('Target a specific card and craft it with Arcana Shards. Earn shards from matches and the season pass: Ordinary 50, Exceptional 150, Elite 200. Uniques and foils stay pack-only.'),
        selector: '[data-tutorial="store-tab-singles"]',
        onEnter: goToTab('singles'),
      },
      {
        key: 'cosmetics-tab',
        title: t('Cosmetics'),
        body: t('Portrait Packs, Background Packs, Playmats, and custom-art Tokens. Pure flair: no gameplay impact, just ways to make your profile, board, and tokens look cooler.'),
        selector: '[data-tutorial="store-tab-cosmetics"]',
        onEnter: goToTab('cosmetics'),
      },
    ];
  }

  handlePurchase = () => {
    playUI(UI.GOLD);
    const { cart } = this.state;
    const { onBuyPack } = this.props;
    for (const setKey of ALL_PRODUCT_KEYS) {
      if (cart[setKey] > 0) onBuyPack(setKey, cart[setKey]);
    }
    this.setState({
      cart: emptyCart(),
      showConfirm: false,
      purchaseFlash: true,
    });
    setTimeout(() => this.setState({ purchaseFlash: false }), 1500);
  };

  render() {
    const { profile, pendingPacks, onBack, onOpenPacks, onToggleMailbox, mailboxUnreadCount, mailboxDropdown, onToggleFriends, friendListData } = this.props;
    const { tab, cart, showConfirm, purchaseFlash } = this.state;
    const debug = isArenaDebugMode();
    const totalPending = pendingPacks?.length || 0;
    const { packs: cartPacks, cost: cartCost } = this.getCartTotal();
    const canAffordCart = debug || profile.coins >= cartCost;

    return (
      <Fragment>
      {/* Root must NOT carry `zoom`. It would scale the background
          canvas along with everything else, and the background uses
          vw/vh math that already tracks the real viewport. Zoom is
          applied only to the AppHeader and UI content wrappers below,
          matching the AuctionHouse pattern. */}
      <div className="fixed inset-0 z-50 flex flex-col" style={{
        background: '#08080a',
      }}>
        {/* Background art + candles share an image-relative coordinate
            system so painted candle positions and flame overlays stay
            locked to the artwork regardless of viewport aspect ratio. */}
        <CoverArtCanvas src="/store-bg.webp" aspectRatio={1672 / 941} wrapperRef={this.canvasRef} />
        {/* Darken overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)' }} />
        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 30%, rgba(0,0,0,0.6) 100%)' }} />

        {/* Torch firelight glow */}
        <StoreTorchFX />

        {/* Candles render in their own coverage canvas AFTER the overlays
            so they sit on top of the darken/vignette/torch layers. The
            transform on CoverArtCanvas creates its own stacking context,
            so children can't escape. The only fix is a second wrapper. */}
        <CoverArtCanvas aspectRatio={1672 / 941}>
          <CandleFlames candles={this.state.candles} />
        </CoverArtCanvas>

        {/* Ambient particles */}
        <AmbientParticles preset="store" />

        {/* Extra darkening for the Card Singles + Disenchant tabs. Blurs
            the busy store background (torches, particles, texture) so
            browsing 1000+ card tiles stays legible. Only active on
            grid-heavy tabs; boosters still get the full atmospheric
            treatment. */}
        {tab === 'singles' || tab === 'disenchant' ? (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.75) 100%)',
            }}
          />
        ) : null}

        {purchaseFlash ? (
          <div className="fixed inset-0 z-[60] pointer-events-none" style={{ background: 'rgba(245,158,11,0.08)', animation: 'fadeOut 1.5s ease-out forwards' }} />
        ) : null}

        {/* ─── TOP BAR ─────────────────────────────── */}
        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          draftQueueDropdown={this.props.draftQueueDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          centerContent={(() => {
            const storeTabs = [
              { id: 'boosters', label: t('Sealed Product') },
              { id: 'singles', label: t('Spellcraft') },
              { id: 'cosmetics', label: t('Cosmetics') },
              { id: 'disenchant', label: t('Disenchant') },
            ];
            return (
              <TabBar
                className="flex items-center"
                variant="underline"
                barInset="1cqw"
                barStyle={{ background: `linear-gradient(90deg, transparent, ${GOLD} 0.6), transparent)`, boxShadow: 'none' }}
                activeIndex={storeTabs.findIndex((st) => st.id === tab)}
              >
                {storeTabs.map((st) => (
                  <button
                    key={st.id}
                    type="button"
                    data-tab-item
                    data-tutorial={
                      st.id === 'boosters' ? 'store-tab-boosters'
                      : st.id === 'singles' ? 'store-tab-singles'
                      : st.id === 'cosmetics' ? 'store-tab-cosmetics'
                      : undefined
                    }
                    className="px-8 py-2.5 text-xs font-semibold"
                    style={{
                      color: tab === st.id ? '#e8d5a0' : 'rgba(166,160,155,0.5)',
                      textShadow: tab === st.id ? '0 0 10px rgba(180,140,60,0.2)' : 'none',
                    }}
                    onClick={() => this.setState({ tab: st.id })}
                  >
                    <span className="arena-heading">{st.label}</span>
                  </button>
                ))}
              </TabBar>
            );
          })()}
        >
          <BackButton title={t('Back to Hub')} onClick={onBack} />
          {/* Per-tab heading + subheading. Lives in the header's left
              cluster, right of the back button, so the content area
              below stays uncluttered. The in-content titles that used
              to sit above each tab's grid were removed. */}
          <div className="flex flex-col min-w-0 leading-tight">
            <span
              className="text-sm font-bold arena-heading truncate"
              style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
            >
              {tab === 'boosters'
                ? this.state.sealedSubTab === 'boxes'
                  ? t('Sealed Boxes')
                  : t('Booster Packs')
                : tab === 'disenchant'
                ? t('Disenchantry')
                : tab === 'cosmetics'
                ? t('Cosmetics')
                : t('Spellcraft Workshop')}
            </span>
            <span
              className="text-[0.625cqw] truncate inline-flex items-center gap-1"
              style={{ color: 'rgba(166,160,155,0.75)' }}
            >
              {tab === 'boosters' ? (
                this.state.sealedSubTab === 'boxes' ? (
                  <>{t('Fixed-content sealed boxes')}</>
                ) : (
                  <>{t('Standard sets and Site-only variants')}</>
                )
              ) : tab === 'disenchant' ? (
                t('Unmake duplicate cards and reclaim their Arcana')
              ) : tab === 'cosmetics' ? (
                t('Portraits, playmats, and other adornments')
              ) : (
                t('Weave chosen spells and sites into being with Arcana')
              )}
            </span>
          </div>
          {totalPending > 0 ? (
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-1.5 text-xs font-semibold transition-all hover:scale-[1.02]"
              style={{ ...GOLD_BTN, borderRadius: 'var(--radius-sm)' }}
              onClick={onOpenPacks}
            >
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              {totalPending === 1 ? t('1 pack to open') : t('{n} packs to open', { n: totalPending })}
            </button>
          ) : null}
        </AppHeader>

        {/* ─── CONTENT ─────────────────────────────── */}
        {/* One persistent wrapper around all four tab bodies. The
            entrance choreography belongs to the screen, not to the tab
. Hanging it off each tab's own root would replay the whole
            settle every time the player clicks Spellcraft or Cosmetics,
            which are among the most-clicked controls in the store. */}
        <motion.div className="relative z-10 flex-1 flex flex-col min-h-0" {...screenPanel(1)}>
        {tab === 'boosters' ? (
          <div className="relative z-10 flex-1 flex flex-col min-h-0 px-6 pb-6 overflow-y-auto">
            {/* Sub-tab bar. Recessed brass-fitted segmented control.
                Anchored at the top of the Sealed Product view so it
                stays put when the user switches between Booster Packs
                and Boxes (different content heights would otherwise
                shift the bar around as it tracks the centered grid). */}
            <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
                <TabBar
                  data-tutorial="store-sealed-subtabs"
                  role="tablist"
                  className="inline-flex items-center p-1"
                  variant="plate"
                  indicatorStyle={{
                    background: `url("/tex-noise.webp"), linear-gradient(180deg, rgba(212,168,67,0.18) 0%, rgba(180,140,55,0.10) 50%, rgba(120,85,30,0.14) 100%)`,
                    border: `1px solid ${GOLD} 0.42)`,
                    boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.18), inset 0 -1px 0 rgba(0,0,0,0.35), 0 0 14px rgba(212,168,67,0.18)',
                  }}
                  activeIndex={SEALED_SUB_TABS.findIndex((st) => st.id === this.state.sealedSubTab)}
                  style={{
                    background: `url("/tex-noise.webp"), linear-gradient(180deg, rgba(6,5,3,0.92) 0%, rgba(12,9,5,0.88) 100%)`,
                    border: `1px solid ${GOLD} 0.22)`,
                    borderRadius: 'var(--radius-md)',
                    boxShadow:
                      'inset 0 2px 6px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(0,0,0,0.4), 0 1px 0 rgba(255,220,140,0.06)',
                  }}
                >
                  {SEALED_SUB_TABS.map((st) => {
                    const active = this.state.sealedSubTab === st.id;
                    return (
                      <button
                        key={st.id}
                        type="button"
                        role="tab"
                        data-tab-item
                        aria-selected={active}
                        className="px-7 py-2 text-[0.6875cqw] font-semibold"
                        style={{
                          color: active ? '#f0d990' : 'rgba(166,160,155,0.55)',
                          background: 'transparent',
                          // Transparent rim keeps the label box identical to
                          // the indicator's, so the plate lands flush on it.
                          border: '1px solid transparent',
                          borderRadius: 'var(--radius-sm)',
                          textShadow: active
                            ? '0 0 10px rgba(232,213,160,0.35), 0 1px 2px rgba(0,0,0,0.7)'
                            : '0 1px 2px rgba(0,0,0,0.7)',
                        }}
                        onClick={() => this.setState({ sealedSubTab: st.id })}
                      >
                        <span className="arena-heading">{t(st.label)}</span>
                      </button>
                    );
                  })}
                </TabBar>
              </div>

            {/* Middle. Fills remaining vertical space and centers the
                product grid for Boosters / Boxes. The Boosters view
                stacks two sections (Standard + Sites Boosters) that
                exceed the available height once the sticky cart panel
                and sub-tab bar are accounted for, so it top-aligns and
                scrolls within its own box. The outer wrapper's
                overflow-y-auto can't pick this up because flex-1 +
                min-h-0 constrains the middle to a fixed height. The
                stepper buttons of the Sites Booster row would otherwise
                sit behind the cart with no way to reach them. */}
            <ScrollArea className="flex-1" viewportClassName="flex justify-center items-start pt-6 pb-6">
              <div className="w-full max-w-[56cqw]">
              {/* Booster grid */}
              {this.state.sealedSubTab === 'boosters' ? (
              <div className="flex flex-col gap-6 mb-8">
                <div>
                  <div
                    className="text-[0.75cqw] font-semibold mb-3 arena-heading"
                    style={{ color: ACCENT_GOLD }}
                  >
                    {t('Standard Boosters')}
                  </div>
              <div data-tutorial="store-booster-grid" className="grid grid-cols-3 gap-8">
                {SET_ORDER.map((setKey, idx) => {
                  const set = BOOSTER_SETS[setKey];
                  const qty = cart[setKey] || 0;
                  const baseScale = BOOSTER_SCALE[setKey] || 1;

                  return (
                    <div key={setKey} className="flex flex-col items-center">
                        <div className="relative mb-4" style={{ width: '12.5cqw', height: '18.75cqw' }}>
                          <button
                            type="button"
                            className="absolute inset-0 flex items-center justify-center cursor-pointer active:scale-95"
                            onClick={() => this.addToCart(setKey)}
                            onMouseEnter={() => this.handlePackEnter(setKey)}
                            onMouseMove={(e) => this.handlePackMove(setKey, e)}
                            onMouseLeave={() => this.handlePackLeave(setKey)}
                          >
                            <img
                              ref={(el) => { this.packImgRefs[setKey] = el; }}
                              src={getBoosterImage(setKey)}
                              alt={t('{set} Booster Pack', { set: set.label })}
                              className="max-w-full max-h-full object-contain pointer-events-none"
                              style={{
                                transform: `perspective(700px) rotateX(0deg) rotateY(0deg) scale(${baseScale})`,
                                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.7))',
                                willChange: 'transform',
                              }}
                              draggable={false}
                            />
                          </button>
                          {qty > 0 ? (
                            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-black z-10" style={{ background: '#d4a843', boxShadow: '0 0 12px rgba(212,168,67,0.4), 0 2px 4px rgba(0,0,0,0.5)' }}>
                              {qty}
                            </div>
                          ) : null}
                        </div>

                      {/* Set name. Dark chip keeps the label legible against busy art */}
                      <div
                        className="text-base font-bold arena-heading mb-2 px-3 py-1"
                        style={{
                          color: '#e8d5a0',
                          textShadow: '0 2px 6px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)',
                          background: 'linear-gradient(180deg, rgba(14,11,8,0.82) 0%, rgba(8,6,4,0.88) 100%)',
                          border: `1px solid ${GOLD} 0.28)`,
                          borderRadius: 'var(--radius-sm)',
                          boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.08), 0 2px 6px rgba(0,0,0,0.5)',
                        }}
                      >
                        {set.label}
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                          style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                          onClick={() => this.setCart(setKey, qty - 1)}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="99"
                          value={qty}
                          className="w-10 h-8 text-center text-sm font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.15)`, borderRadius: 'var(--radius-sm)', color: '#e8d5a0' }}
                          onInput={(e) => this.setCart(setKey, e.target.value)}
                        />
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                          style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                          onClick={() => this.setCart(setKey, qty + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
                </div>

                <div>
                  <div
                    className="text-[0.75cqw] font-semibold mb-3 arena-heading"
                    style={{ color: ACCENT_GOLD }}
                  >
                    {t('Site Boosters')}
                  </div>
                  <div className="grid grid-cols-3 gap-8">
                    {SITES_BOOSTER_ORDER.map((packKey) => {
                      const pack = SITES_BOOSTERS[packKey];
                      if (!pack) return null;
                      const qty = cart[packKey] || 0;
                      return (
                        <div key={packKey} className="flex flex-col items-center">
                          <button
                            type="button"
                            className="relative mb-3 flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-[1.03] active:scale-95"
                            onClick={() => this.addToCart(packKey)}
                          >
                            <ProductPreview setKey={packKey} width="16cqw" height="12cqw" />
                            {qty > 0 ? (
                              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-black z-10" style={{ background: '#d4a843', boxShadow: '0 0 12px rgba(212,168,67,0.4), 0 2px 4px rgba(0,0,0,0.5)' }}>
                                {qty}
                              </div>
                            ) : null}
                          </button>

                          <div
                            className="text-sm font-bold arena-heading mb-1 px-3 py-1"
                            style={{
                              color: '#e8d5a0',
                              textShadow: '0 2px 6px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)',
                              background: 'linear-gradient(180deg, rgba(14,11,8,0.82) 0%, rgba(8,6,4,0.88) 100%)',
                              border: `1px solid ${GOLD} 0.28)`,
                              borderRadius: 'var(--radius-sm)',
                              boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.08), 0 2px 6px rgba(0,0,0,0.5)',
                            }}
                          >
                            {pack.label}
                          </div>

                          <div
                            className="text-[0.625cqw] text-center mb-2"
                            style={{ color: 'rgba(166,160,155,0.75)', lineHeight: 1.45 }}
                          >
                            {t('15 random sites · foil chance')}
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              className="w-7 h-7 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                              style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                              onClick={() => this.setCart(packKey, qty - 1)}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min="0"
                              max="99"
                              value={qty}
                              className="w-10 h-7 text-center text-sm font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.15)`, borderRadius: 'var(--radius-sm)', color: '#e8d5a0' }}
                              onInput={(e) => this.setCart(packKey, e.target.value)}
                            />
                            <button
                              type="button"
                              className="w-7 h-7 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                              style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                              onClick={() => this.setCart(packKey, qty + 1)}
                            >
                              +
                            </button>
                          </div>

                          <div
                            className="mt-1 text-[0.6875cqw] tabular-nums inline-flex items-center gap-1"
                            style={{ color: '#f0d060' }}
                          >
                            <CoinIcon size={11} />
                            {pack.price}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              ) : (
              <div className="flex flex-col gap-6 mb-8">
                <div>
                  <div
                    className="text-[0.75cqw] font-semibold mb-3 arena-heading"
                    style={{ color: ACCENT_GOLD }}
                  >
                    {t('Sealed Boxes')}
                  </div>
                <div className="grid grid-cols-3 gap-8 items-end justify-center">
                  {BOX_ORDER.map((boxKey) => {
                    const box = SEALED_BOXES[boxKey];
                    if (!box) return null;
                    const qty = cart[boxKey] || 0;
                    return (
                      <div key={boxKey} className="flex flex-col items-center col-start-2">
                        <button
                          type="button"
                          className="relative mb-4 flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-[1.03] active:scale-95"
                          style={{ width: '30cqw', height: '27.5cqw' }}
                          onClick={() => this.addToCart(boxKey)}
                        >
                          <img
                            src={getBoxImage(boxKey)}
                            alt={box.label}
                            className="max-w-full max-h-full object-contain"
                            style={{
                              filter: 'drop-shadow(0 18px 40px rgba(0,0,0,0.8))',
                            }}
                            draggable={false}
                          />
                          {qty > 0 ? (
                            <div className="absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-black" style={{ background: '#d4a843', boxShadow: '0 0 14px rgba(212,168,67,0.45), 0 2px 4px rgba(0,0,0,0.5)' }}>
                              {qty}
                            </div>
                          ) : null}
                        </button>

                        <div
                          className="text-base font-bold arena-heading mb-2 px-3 py-1"
                          style={{
                            color: '#e8d5a0',
                            textShadow: '0 2px 6px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)',
                            background: 'linear-gradient(180deg, rgba(14,11,8,0.82) 0%, rgba(8,6,4,0.88) 100%)',
                            border: `1px solid ${GOLD} 0.28)`,
                            borderRadius: 'var(--radius-sm)',
                            boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.08), 0 2px 6px rgba(0,0,0,0.5)',
                          }}
                        >
                          {box.label}
                        </div>

                        <div
                          className="text-[0.6875cqw] text-center mb-3 max-w-[26.25cqw]"
                          style={{ color: 'rgba(166,160,155,0.75)', lineHeight: 1.45 }}
                        >
                          {box.description}
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                            style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                            onClick={() => this.setCart(boxKey, qty - 1)}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="99"
                            value={qty}
                            className="w-10 h-8 text-center text-sm font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.15)`, borderRadius: 'var(--radius-sm)', color: '#e8d5a0' }}
                            onInput={(e) => this.setCart(boxKey, e.target.value)}
                          />
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                            style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                            onClick={() => this.setCart(boxKey, qty + 1)}
                          >
                            +
                          </button>
                        </div>

                        <div
                          className="mt-2 text-[0.6875cqw] tabular-nums inline-flex items-center gap-1"
                          style={{ color: '#f0d060' }}
                        >
                          <CoinIcon size={11} />
                          {t('{price} per box', { price: box.price })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>

                <div>
                  <div
                    className="text-[0.75cqw] font-semibold mb-3 arena-heading"
                    style={{ color: ACCENT_GOLD }}
                  >
                    {t('Preconstructed Decks')}
                  </div>
                <div className="grid grid-cols-4 gap-6 items-end justify-center">
                  {ALPHA_PRECON_ORDER.map((preconKey) => {
                    const precon = ALPHA_PRECONS[preconKey];
                    if (!precon) return null;
                    const qty = cart[preconKey] || 0;
                    return (
                      <div key={preconKey} className="flex flex-col items-center">
                        <div
                          className="relative mb-4 flex items-end justify-center cursor-pointer"
                          style={{ height: '15.5cqw' }}
                          onClick={() => this.addToCart(preconKey)}
                        >
                          <PreconDeckBox3D
                            artSlug={precon.avatarSlug}
                            wordmark="Sorcery"
                            subtitle={precon.subtitle}
                            accent={precon.accent}
                          />
                          {qty > 0 ? (
                            <div className="absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-black" style={{ background: '#d4a843', boxShadow: '0 0 14px rgba(212,168,67,0.45), 0 2px 4px rgba(0,0,0,0.5)' }}>
                              {qty}
                            </div>
                          ) : null}
                        </div>

                        <div
                          className="text-base font-bold arena-heading mb-3 px-3 py-1 whitespace-nowrap text-center"
                          style={{
                            color: '#e8d5a0',
                            textShadow: '0 2px 6px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)',
                            background: 'linear-gradient(180deg, rgba(14,11,8,0.82) 0%, rgba(8,6,4,0.88) 100%)',
                            border: `1px solid ${GOLD} 0.28)`,
                            borderRadius: 'var(--radius-sm)',
                            boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.08), 0 2px 6px rgba(0,0,0,0.5)',
                          }}
                        >
                          {precon.label}
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                            style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                            onClick={() => this.setCart(preconKey, qty - 1)}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="0"
                            max="99"
                            value={qty}
                            className="w-10 h-8 text-center text-sm font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.15)`, borderRadius: 'var(--radius-sm)', color: '#e8d5a0' }}
                            onInput={(e) => this.setCart(preconKey, e.target.value)}
                          />
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-90"
                            style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                            onClick={() => this.setCart(preconKey, qty + 1)}
                          >
                            +
                          </button>
                        </div>

                        <div
                          className="mt-2 text-[0.6875cqw] tabular-nums inline-flex items-center gap-1"
                          style={{ color: '#f0d060' }}
                        >
                          <CoinIcon size={11} />
                          {t('{price} per deck', { price: ALPHA_PRECON_PRICE })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>
              )}
              </div>
            </ScrollArea>

            {/* Cart. Anchored at the bottom of the Sealed Product
                view so it stays put regardless of which sub-tab is
                active or how tall the product grid is. */}
            <div className="w-full max-w-[56cqw] mx-auto shrink-0">
              {/* Cart summary */}
              <div className="relative p-5 flex items-center justify-between" style={{
                background: PANEL_BG,
                backdropFilter: 'blur(8px)',
                border: `1px solid ${cartPacks > 0 ? `${GOLD} 0.35)` : `${GOLD} 0.15)`}`,
                borderRadius: 'var(--radius-lg)',
                boxShadow: cartPacks > 0 ? `0 0 20px ${GOLD} 0.08)` : 'none',
              }}>
                <CornerPlating position="top-left" />
                <CornerPlating position="top-right" />
                <CornerPlating position="bottom-left" />
                <CornerPlating position="bottom-right" />
                <div>
                  <div className="text-sm font-semibold" style={{ color: cartPacks > 0 ? '#e8d5a0' : 'rgba(166,160,155,0.4)' }}>
                    {cartPacks > 0 ? (cartPacks === 1 ? t('1 item selected') : t('{n} items selected', { n: cartPacks })) : t('Nothing selected')}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: 'rgba(166,160,155,0.35)' }}>
                    {cartPacks > 0
                      ? ALL_PRODUCT_KEYS
                          .filter((k) => cart[k] > 0)
                          .map((k) => `${cart[k]}× ${getProductLabel(k)}`)
                          .join(', ')
                      : t('Click a product or use +/− to add')}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-lg font-bold tabular-nums flex items-center justify-end gap-1.5" style={{ color: cartPacks > 0 ? '#f0d060' : 'rgba(166,160,155,0.2)' }}>
                      {cartPacks > 0 ? <CoinIcon size={14} /> : null}
                      {cartCost}
                    </div>
                    <div className="text-[0.625cqw]" style={{ color: 'rgba(166,160,155,0.3)' }}>{t('gold')}</div>
                  </div>
                  <button
                    type="button"
                    data-tutorial="store-purchase"
                    disabled={cartPacks === 0 || !canAffordCart}
                    className="px-8 py-3 text-sm font-semibold arena-heading transition-all hover:scale-[1.03] active:scale-[0.97]"
                    style={cartPacks > 0 && canAffordCart
                      ? GOLD_BTN
                      : {
                          ...BEVELED_BTN,
                          color: 'rgba(166,160,155,0.25)',
                        }
                    }
                    data-sound={UI.CONFIRM}
                    onClick={() => this.setState({ showConfirm: true })}
                  >
                    {cartPacks === 0 ? t('Purchase') : canAffordCart ? t('Purchase') : t('Not enough gold')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'singles' ? (
          <div className="relative z-10 flex-1 flex flex-col min-h-0 px-6 py-4">
            <StoreSinglesTab
              sorceryCards={this.props.sorceryCards}
              profile={profile}
              onProfileUpdate={this.props.onProfileUpdate}
            />
          </div>
        ) : null}

        {tab === 'disenchant' ? (
          <div className="relative z-10 flex-1 flex flex-col min-h-0 px-6 py-4">
            <StoreDisenchantTab
              sorceryCards={this.props.sorceryCards}
              profile={profile}
              savedDecks={this.props.savedDecks}
              onProfileUpdate={this.props.onProfileUpdate}
            />
          </div>
        ) : null}

        {tab === 'cosmetics' ? (
          <div className="relative z-10 flex-1 flex flex-col min-h-0">
            <StoreCosmeticsTab
              profile={profile}
              sorceryCards={this.props.sorceryCards}
              onAfterPurchase={(cardId) => {
                // Server fires a profile:update over WS that carries
                // the new ownedCosmetics list + reduced balances, so
                // the local arenaProfile syncs automatically. Nothing
                // to do here beyond the patch the tab already made to
                // its own owned-set for instant Owned-badge feedback.
                void cardId;
              }}
            />
          </div>
        ) : null}
        </motion.div>

        {/* ─── CONFIRMATION DIALOG ─────────────────── */}
        {showConfirm ? (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-sm" onClick={() => this.setState({ showConfirm: false })}>
            <div
              className="relative w-96 p-6"
              style={{ background: PANEL_BG, backdropFilter: 'blur(8px)', border: `1px solid ${PANEL_BORDER}`, borderRadius: 'var(--radius-lg)', boxShadow: `0 0 40px rgba(0,0,0,0.5), 0 0 20px ${GOLD} 0.05)` }}
              onClick={(e) => e.stopPropagation()}
            >
              <CornerPlating position="top-left" />
              <CornerPlating position="top-right" />
              <CornerPlating position="bottom-left" />
              <CornerPlating position="bottom-right" />

              <h2 className="text-lg font-bold arena-heading mb-4" style={{ color: '#e8d5a0' }}>{t('Confirm Purchase')}</h2>
              <div className="flex flex-col gap-2 mb-4">
                {ALL_PRODUCT_KEYS.filter((k) => cart[k] > 0).map((k) => (
                  <div key={k} className="flex items-center justify-between text-sm py-1" style={{ borderBottom: `1px solid ${GOLD} 0.08)` }}>
                    <div className="flex items-center gap-3">
                      <img src={getProductImage(k)} alt="" className="w-8 h-auto" draggable={false} />
                      <span style={{ color: '#A6A09B' }}>{cart[k]}× {getProductLabel(k)}</span>
                    </div>
                    <span className="tabular-nums font-semibold inline-flex items-center gap-1.5" style={{ color: '#f0d060' }}>
                      <CoinIcon size={12} />
                      {cart[k] * getProductUnitPrice(k)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between py-3 mb-5" style={{ borderTop: `1px solid ${GOLD} 0.15)` }}>
                <span className="text-sm font-semibold" style={{ color: '#e8d5a0' }}>{t('Total')}</span>
                <span className="text-lg font-bold tabular-nums inline-flex items-center gap-1.5" style={{ color: '#f0d060' }}>
                  <CoinIcon size={14} />
                  {cartCost}
                </span>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  className="flex-1 py-2.5 text-sm font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ ...BEVELED_BTN, background: `url("/tex-noise.webp"), url("/tex-stone.webp"), rgba(20, 16, 10, 0.95)`, borderRadius: 'var(--radius-sm)', color: '#A6A09B' }}
                  data-sound={UI.CANCEL}
                  onClick={() => this.setState({ showConfirm: false })}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  className="flex-1 py-2.5 text-sm font-semibold arena-heading transition-all hover:scale-[1.02] active:scale-[0.97]"
                  style={{ ...GOLD_BTN, borderRadius: 'var(--radius-sm)' }}
                  data-sound={UI.CONFIRM}
                  onClick={this.handlePurchase}
                >
                  {cartPacks === 1 ? t('Buy 1 Item') : t('Buy {n} Items', { n: cartPacks })}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <style>{`
          @keyframes fadeOut {
            0% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>

        {/* First-run store tutorial. */}
        {this.state.showStoreTutorial && (
          <TutorialOverlay
            steps={this.buildTutorialSteps()}
            onDismiss={this.handleStoreTutorialDismiss}
          />
        )}
      </div>
      {/* Dev-only candle editor. Rendered OUTSIDE the zoomed wrapper so
          its `position: fixed` UI is anchored to the actual viewport,
          not the zoomed containing block. The catcher overlays the
          canvas via getCanvasRect(). */}
      {isDevBuild() ? (
        <CandlePlacementDev
          sceneKey="store"
          defaultCandles={STORE_DEFAULT_CANDLES}
          onChange={(candles) => this.setState({ candles })}
          getCanvasRect={() => this.canvasRef.current?.getBoundingClientRect()}
        />
      ) : null}
      </Fragment>
    );
  }
}
